import {h, Fragment} from "preact"
import { useFacets, useAppStateSelector, pick } from "@nosto/preact"
import { useState } from "preact/hooks"
import Facet from "./Facet"
import RangeFacet from "./RangeFacet"

export const toggleButtonId = "toggle-mobile-sidebar"

function ToggleSidebarButton({ className }) {
    return (
        <label
            className={`ns-act-btn ns-close-sidebar ns-d-block ns-clickable`}
            for={toggleButtonId}
        >
            <svg xmlns='http://www.w3.org/2000/svg' width="16" height="16" viewBox="0 0 18 18" aria-hidden='true' focusable='false' role='presentation' fill='none'>
                <path d="M17 1L1.22665 16.7733L17 1Z" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round"/>
                <path d="M16.7733 16.7733L1 1L16.7733 16.7733Z" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round"/>
            </svg>
        </label>
    )
}

export default function SideBar() {
    const { loading, facets } = useFacets()

    const { response } = useAppStateSelector(state => pick(state, "response"))
    const docCount = response.products?.total ?? 0
    const totalSummary = docCount === 1 ? `${docCount} Piece` : `${docCount} Pieces`

    const [pendingChanges, setPendingChanges] = useState({})

    const updatePendingChange = (facetField, value, selected) => {
        setPendingChanges(prev => {
            const newChanges = { ...prev }
            
            if (!newChanges[facetField]) {
                newChanges[facetField] = {}
            }

            newChanges[facetField][value] = selected
            
            return newChanges
        })
    }

    const isValueSelected = (facetField, value, originalSelected) => {
        if (pendingChanges[facetField] && pendingChanges[facetField].hasOwnProperty(value)) {
            return pendingChanges[facetField][value]
        }
        return originalSelected
    }

    const applyFilters = () => {
        Object.entries(pendingChanges).forEach(([facetField, values]) => {
            if (window.facetToggleFunctions && window.facetToggleFunctions[facetField]) {
                const toggleFunction = window.facetToggleFunctions[facetField]
                Object.entries(values).forEach(([value, shouldBeSelected]) => {
                    toggleFunction(facetField, value, shouldBeSelected)
                })
            }
        })

        setPendingChanges({})
    }

    const clearAll = () => {
        setPendingChanges({})
        
        facets?.forEach(facet => {
            if (window.facetToggleFunctions && window.facetToggleFunctions[facet.field]) {
                const toggleFunction = window.facetToggleFunctions[facet.field]
                facet.data?.forEach(value => {
                    if (value.selected) {
                        toggleFunction(facet.field, value.value, false)
                    }
                })
            }
        })
    }

    return facets?.length > 0 ? (
        <>
            <input type="checkbox" id={toggleButtonId} class="ns-facet-sidebar-toggle ns-d-none" />
            <label
                class="ns-sidebar-backdrop ns-fixed ns-w-100 ns-h-100 bg-t-brand-foreground-secondary"
                for={toggleButtonId}
            />
            <div
                class="ns-sidebar-wrapper ns-h-100 ns-col-3 ns-col-l-2"
                style={loading ? "opacity: 0.3;" : ""}
            >
                <div class="ns-sidebar-content ns-relative ns-d-flex ns-flex-column ns-background-white ns-h-100 ns-mr-0 ns-mr-md-auto">
                    <div class="ns-sidebar-header ns-d-flex ns-justify-content-between ns-align-items-center">
                        <h5 class="h3 font-medium tracking-[0.03em] uppercase text-t-brand-foreground lg:leading-[1.2] lg:tracking-[0.02em]">Filters</h5>
                        <ToggleSidebarButton />
                    </div>
                    <div class="ns-sidebar-menu ns-h-100">
                        <ul class="ns-list-unstyled ns-d-flex ns-flex-column gap-y-lg ns-w-100 ns-gap-l-y-7.5">
                            {facets?.map(facet => {
                                switch (facet.type) {
                                    case "terms":
                                        return (
                                            <Facet 
                                                key={facet.id} 
                                                facet={facet}
                                                updatePendingChange={updatePendingChange}
                                                isValueSelected={isValueSelected}
                                            />
                                        )
                                    case "stats":
                                        return <RangeFacet key={facet.id} facet={facet} />
                                    default:
                                        return null
                                }
                            })}
                        </ul>
                    </div>
                    <div class="ns-sidebar-footer">
                        <button
                            class="utility block w-full h-[3.125rem] p-xs bg-t-brand-foreground text-p-lightest border border-solid border-t-brand-accent-02"
                            onClick={applyFilters}
                        >
                            Apply Filter
                        </button>
                        <div class="flex items-center justify-between mt-3.5 lg:justify-center">
                            <button
                                class="utility pb-0.5 text-t-brand-foreground border-b border-t-brand-accent-02 transition-[color, border-color] hover:no-underline hover:text-t-brand-foreground-secondary hover:border-t-brand-foreground-secondary focus:text-t-brand-foreground-secondary focus:border-u-focus"
                                onClick={clearAll}
                            >
                                Clear All
                            </button>
                            <span class="body-l text-t-brand-foreground lg:hidden">
                                {totalSummary}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </>
    ) : (
        ""
    )
}
